import type { JWTClaims } from '@scrolldex/shared';
import { ref, computed } from 'vue';
import { useLocalStorage } from '@vueuse/core';

/**
 * Studio auth client. Mirrors the admin client's token/refresh handling, but
 * gates on studio access (studioAccess OR isAdmin) instead of isAdmin, and adds
 * a Discord OAuth login flow.
 *
 * The login gate is a production-only concern: `App.vue` bypasses it entirely
 * under `import.meta.env.DEV`, so this client is never exercised in `pnpm dev`.
 */

const isDev = import.meta.env.DEV;

// In dev, talk to the local API; in a built/deployed studio, the prod API.
const API_URL = isDev ? 'http://localhost:3000' : 'https://api.sigdex.io';
// Where Discord sends the browser back after OAuth. Must be whitelisted in the
// Discord app's redirect URIs.
const UI_URL = isDev ? 'http://localhost:5175' : 'https://studio.sigdex.io';
const DISCORD_REDIRECT_URI = `${UI_URL}/discord/login-callback`;

const ACCESS_TOKEN_KEY = 'studio_access_token';
const REFRESH_TOKEN_KEY = 'studio_refresh_token';

export const accessToken = useLocalStorage<string | null>(ACCESS_TOKEN_KEY, null);
export const claims = ref<JWTClaims | null>(null);
export const isAuthenticated = ref<boolean>(false);

/** A user may use the studio if they have studio access or are an admin. */
export function claimsHaveAccess(c: JWTClaims | null): boolean {
  return !!c && (c.studioAccess || c.isAdmin);
}

/** Whether the current user is allowed into the studio. */
export const hasAccess = computed(() => claimsHaveAccess(claims.value));

// Decode base64url with proper UTF-8 support (usernames may be non-ASCII).
function base64UrlDecode(base64Url: string): string {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new TextDecoder('utf-8').decode(bytes);
}

function parseClaims(token: string | null): JWTClaims | null {
  if (!token) return null;
  try {
    const [, payloadBase64] = token.split('.');
    return JSON.parse(base64UrlDecode(payloadBase64)) as JWTClaims;
  } catch {
    return null;
  }
}

export class StudioClient {
  private apiBaseUrl = API_URL.replace(/\/$/, '');
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    this.loadTokens();
  }

  private isAccessTokenExpired(leeway = 30): boolean {
    const parsed = parseClaims(this.accessToken);
    if (!parsed?.exp) return true;
    const now = Math.floor(Date.now() / 1000);
    return parsed.exp < now + leeway;
  }

  // Adopt tokens from localStorage on boot — but only keep them if the user
  // still has studio access.
  private loadTokens(): void {
    this.accessToken = accessToken.value;
    this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    const parsed = parseClaims(this.accessToken);
    if (claimsHaveAccess(parsed)) {
      claims.value = parsed;
      isAuthenticated.value = true;
    } else {
      this.clearTokens();
    }
  }

  // Persist tokens — throws (and clears) if the user lacks studio access.
  private saveTokens(newAccessToken: string, newRefreshToken: string): void {
    const parsed = parseClaims(newAccessToken);
    if (!claimsHaveAccess(parsed)) {
      this.clearTokens();
      throw new Error('Access denied: studio access required');
    }

    this.accessToken = newAccessToken;
    this.refreshToken = newRefreshToken;
    accessToken.value = newAccessToken;
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

    claims.value = parsed;
    isAuthenticated.value = true;
  }

  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    accessToken.value = null;
    localStorage.removeItem(REFRESH_TOKEN_KEY);

    claims.value = null;
    isAuthenticated.value = false;
  }

  logout(): void {
    this.clearTokens();
  }

  // Email/username + password login. Throws if the user lacks studio access.
  async login(emailOrUsername: string, password: string): Promise<void> {
    const res = await fetch(`${this.apiBaseUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailOrusername: emailOrUsername, password }),
    });
    if (!res.ok) throw new Error(await res.text());

    const { accessToken: at, refreshToken: rt } = await res.json();
    this.saveTokens(at, rt);
  }

  // Kick off Discord OAuth: fetch the authorize URL and redirect the browser.
  async startDiscordLogin(): Promise<void> {
    const res = await fetch(
      `${this.apiBaseUrl}/discord/login-url?redirect_uri=${encodeURIComponent(
        DISCORD_REDIRECT_URI,
      )}`,
    );
    if (!res.ok) throw new Error(await res.text());
    const { url } = await res.json();
    window.location.href = url;
  }

  // Exchange the Discord callback code for tokens. Throws if the user lacks
  // studio access.
  async completeDiscordLogin(code: string, state: string | null): Promise<void> {
    const res = await fetch(`${this.apiBaseUrl}/discord/login-callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, state, redirect_uri: DISCORD_REDIRECT_URI }),
    });
    if (!res.ok) throw new Error(await res.text());

    const { accessToken: at, refreshToken: rt } = await res.json();
    this.saveTokens(at, rt);
  }
}

export function parseErrorMessage(error: any): string {
  try {
    const msg = JSON.parse(error.message || '{}');
    return msg.error || error.message || 'An unknown error occurred';
  } catch {
    return error.message || 'An unknown error occurred';
  }
}

export const client = new StudioClient();
